import { useState, useRef, useEffect } from 'react';
import './FunctionRow.css';
import IconButton from '/src/components/UI/Button/IconButton/IconButton';
import VariableDropdown from '../../../Variables/VariablesDropdown/VariablesDropdown';
import AutoComplete from '/src/components/UI/AutoComplete/AutoComplete';
import { useBotStateContext } from '../../../../contexts/BotContext';
import FunctionSearchDropdown from '../FunctionSearchDropdown/FunctionSearchDropdown';
import formatParamName from '../../../../utils/formatParamName';

function removeVariadicParamValue(paramValues, keyToRemove, baseName) {
    const updated = { ...paramValues };
    delete updated[keyToRemove];

    const reindexed = {};
    let variadicCount = 0;
    Object.keys(updated).forEach((k) => {
        const isVariadicFamily = k === baseName || k.match(new RegExp('^' + baseName + '\\d+$'));
        if (isVariadicFamily) {
            reindexed[variadicCount === 0 ? baseName : baseName + variadicCount] = updated[k];
            variadicCount++;
        } else {
            reindexed[k] = updated[k];
        }
    });

    return reindexed;
}

function FunctionRow({ index, idx, first, value, params = [], variable = null, functions = [], setValue, updateRow, deleteValue, move, autoOpen = false }) {
    const { variables } = useBotStateContext();

    const [localSelectedFn, setLocalSelectedFn] = useState(() => functions.find((fn) => fn.call_name === value) || null);
    const [activeParam, setActiveParam] = useState(null);
    const [open, setOpen] = useState(!!value || autoOpen);
    const [isHover, setIsHover] = useState(false);
    const [variadicRevision, setVariadicRevision] = useState(0);
    const [paramValues, setParamValues] = useState(() => {
        const initial = {};
        (params || []).forEach(p => { initial[p.name] = p.value ?? ''; });
        return initial;
    });
    const containerRef = useRef();

    useEffect(() => {
        const fn = functions.find((f) => f.call_name === value) || null;
        setLocalSelectedFn(fn);
        if (!fn) return;
        const restored = {};
        (fn.params || []).forEach(apiParam => {
            const saved = (params || []).find(p => p.name === apiParam.name);
            restored[apiParam.name] = (saved && saved.value != null) ? saved.value : (apiParam.default ?? '');
        });
        (params || []).forEach(savedParam => {
            if (!(savedParam.name in restored)) {
                restored[savedParam.name] = savedParam.value ?? '';
            }
        });
        setParamValues(restored);
    }, [functions, value]);

    const onSelect = (fn) => {
        const initialParams = (fn.params || []).map(p => ({ name: p.name, value: p.default || '' }));
        setLocalSelectedFn(fn);
        setParamValues(Object.fromEntries(initialParams.map(p => [p.name, p.value])));
        updateRow(index, { value: fn.call_name, params: initialParams });
    };

    const onParamChange = (paramName, val) => {
        const updated = { ...paramValues, [paramName]: val };
        setParamValues(updated);
        const paramsList = Object.keys(updated).map(name => ({ name, value: updated[name] ?? '' }));
        setValue(index, 'params', paramsList);
    };

    const onVariableSelect = (_name, variableObj) => {
        setValue(index, 'variable', variableObj || null);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {}
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fnParams = localSelectedFn ? (localSelectedFn.params || []) : [];

    return (
        <div className="function_row" ref={containerRef} onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
            <div className="function_row_body">
                <div className="function_variable_row" onMouseDown={(e) => e.stopPropagation()}>
                    <VariableDropdown key={variable?.id ?? 'none'} variable={variable} setValue={onVariableSelect} name={`fn_variable_${index}`} resetVariableDisable={false} createVariableDisable={true}/>
                    <div className="actions">
                        {isHover && idx !== 0 && first !== true && <IconButton onClick={() => move(index, index - 1)} className="editor-iconButton main_add-button button_center profil_action" icon="fas fa-chevron-up"/>}
                        {isHover && idx !== -1 && <IconButton onClick={() => move(index, index + 1)} className="editor-iconButton main_add-button button_center profil_action" icon="fas fa-chevron-down"/>}
                        <IconButton onClick={() => setOpen(o => !o)} className="editor-iconButton main_add-button button_center edit_action" icon="fas fa-edit"/>
                        <IconButton className="editor-iconButton main_add-button button_center delete_action" icon="fas fa-trash" onClick={() => deleteValue(index)}/>
                    </div>
                </div>

                {open && (
                    <>
                        <div className="function_row_top">
                            <FunctionSearchDropdown functions={functions} value={value} onSelect={onSelect} />
                        </div>

                        {fnParams.length > 0 && (
                            <div className="function_params">
                                <div className="function_params_title">
                                    Parameters
                                    {fnParams.some(p => p.variadic) && (
                                        <IconButton
                                            className="editor-iconButton main_add-button button_center"
                                            icon="fas fa-plus"
                                            onClick={() => {
                                                const variadicParam = fnParams.find(p => p.variadic);
                                                const extraKeys = Object.keys(paramValues).filter(
                                                    k => k === variadicParam.name || k.match(new RegExp('^' + variadicParam.name + '\\d+$'))
                                                );
                                                const next = variadicParam.name + (extraKeys.length || 1);
                                                onParamChange(next, '');
                                            }}
                                        />
                                    )}
                                </div>
                                {fnParams.map((param) => {
                                    const extraKeys = Object.keys(paramValues).filter(
                                        k => k === param.name || k.match(new RegExp('^' + param.name + '\\d+$'))
                                    );
                                    const keys = param.variadic ? (extraKeys.length ? extraKeys : [param.name]) : [param.name];

                                    if (param.variadic) {
                                        return keys.map((key, i) => (
                                            <div key={key} className="function_param_row">
                                                <label className="function_param_name">
                                                    {formatParamName(param.name)}{i > 0 ? i : ''}
                                                    {param.required && i === 0 && <span className="function_param_required"> *</span>}
                                                </label>
                                                <div className="function_param_input_row">
                                                    <div
                                                        onFocus={() => setActiveParam(key)}
                                                        onBlur={(e) => { const t = e.currentTarget; setTimeout(() => { if (!t.contains(document.activeElement)) setActiveParam(null); }, 150); }}
                                                        className={activeParam !== key ? 'autocomplete-collapsed' : ''}
                                                    >
                                                        <AutoComplete
                                                            key={key + '_' + variadicRevision + '_' + (localSelectedFn?.call_name || '')}
                                                            value={String(paramValues[key] ?? '')}
                                                            items={variables}
                                                            getNameFromItem={(item) => '[' + item.name.toUpperCase() + ']'}
                                                            getItemValue={(_name, val) => {
                                                                const stored = val && typeof val === 'object'
                                                                    ? '[' + val.name.toUpperCase() + ']'
                                                                    : val;
                                                                onParamChange(key, stored);
                                                            }}
                                                            shouldItemRender={(nameItem, v) =>
                                                                v.length > 0 && nameItem.toLowerCase().includes(v.toLowerCase())
                                                            }
                                                            placeholder={param.default || ''}
                                                            className="function_param_input"
                                                            closeOnBlur={true}
                                                        />
                                                    </div>
                                                    {i > 0 ? (
                                                        <IconButton
                                                            className="editor-iconButton main_add-button button_center delete_action"
                                                            icon="fas fa-trash"
                                                            onClick={() => {
                                                                const reindexed = removeVariadicParamValue(paramValues, key, param.name);
                                                                setParamValues(reindexed);
                                                                setVariadicRevision(r => r + 1);
                                                                const paramsList = Object.keys(reindexed).map(n => ({ name: n, value: reindexed[n] ?? '' }));
                                                                setValue(index, 'params', paramsList);
                                                            }}
                                                        />
                                                    ) : <span />}
                                                </div>
                                            </div>
                                        ));
                                    }

                                    return (
                                        <div key={param.name} className="function_param_row">
                                            <label className="function_param_name">
                                                {formatParamName(param.name)}
                                                {param.required && <span className="function_param_required"> *</span>}
                                            </label>
                                            <div
                                                onFocus={() => setActiveParam(param.name)}
                                                onBlur={(e) => { const t = e.currentTarget; setTimeout(() => { if (!t.contains(document.activeElement)) setActiveParam(null); }, 150); }}
                                                className={activeParam !== param.name ? 'autocomplete-collapsed' : ''}
                                            >
                                                <AutoComplete
                                                    key={param.name + '_' + (localSelectedFn?.call_name || '')}
                                                    value={String(paramValues[param.name] ?? '')}
                                                    items={variables}
                                                    getNameFromItem={(item) => '[' + item.name.toUpperCase() + ']'}
                                                    getItemValue={(_name, val) => {
                                                        const stored = val && typeof val === 'object'
                                                            ? '[' + val.name.toUpperCase() + ']'
                                                            : val;
                                                        onParamChange(param.name, stored);
                                                    }}
                                                    shouldItemRender={(nameItem, v) =>
                                                        v.length > 0 && nameItem.toLowerCase().includes(v.toLowerCase())
                                                    }
                                                    placeholder={param.default || ''}
                                                    className="function_param_input"
                                                    closeOnBlur={true}
                                                />
                                            </div>
                                            <span />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default FunctionRow;
